/**
 * Pure validation functions for the sign-up form.
 * No React, no side effects — input → output only.
 */
import {
  NAME_MIN_LENGTH,
  NAME_MAX_LENGTH,
  USERNAME_MIN_LENGTH,
  USERNAME_MAX_LENGTH,
  EMAIL_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
} from '../../constants';
import {
  createClientValidationError,
  type ClientValidationError,
} from '../../services/utils/errorHandler';

export interface FieldValidation {
  validateStatus: 'error' | 'success' | '';
  errorMsg: string | null;
}

export const FIELD_VALIDATION_EMPTY: FieldValidation = {
  validateStatus: '',
  errorMsg: null,
};

export function validateName(name: string): FieldValidation {
  if (name.length < NAME_MIN_LENGTH) {
    return {
      validateStatus: 'error',
      errorMsg: `Name is too short (Minimum ${NAME_MIN_LENGTH} characters needed.)`,
    };
  }
  if (name.length > NAME_MAX_LENGTH) {
    return {
      validateStatus: 'error',
      errorMsg: `Name is too long (Maximum ${NAME_MAX_LENGTH} characters allowed.)`,
    };
  }
  return { validateStatus: 'success', errorMsg: null };
}

export function validateUsername(username: string): FieldValidation {
  if (username.length < USERNAME_MIN_LENGTH) {
    return {
      validateStatus: 'error',
      errorMsg: `Username is too short (Minimum ${USERNAME_MIN_LENGTH} characters needed.)`,
    };
  }
  if (username.length > USERNAME_MAX_LENGTH) {
    return {
      validateStatus: 'error',
      errorMsg: `Username is too long (Maximum ${USERNAME_MAX_LENGTH} characters allowed.)`,
    };
  }
  const missingUsernameRequirements = [];
  if (/[!@#$%^&*(),.?":{}|<>]/.test(username)) {
    missingUsernameRequirements.push(`special character`);
  }
  if (missingUsernameRequirements.length > 0) {
    return {
      validateStatus: 'error',
      errorMsg: `Username must not contain ${missingUsernameRequirements.join(`, `)}`,
    };
  }
  return { validateStatus: 'success', errorMsg: null };
}

export function validateEmail(email: string): FieldValidation {
  if (email.length > EMAIL_MAX_LENGTH) {
    return {
      validateStatus: 'error',
      errorMsg: `Email is too long (Maximum ${EMAIL_MAX_LENGTH} characters allowed.)`,
    };
  }
  return { validateStatus: 'success', errorMsg: null };
}

export function validatePassword(password: string): FieldValidation {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return {
      validateStatus: 'error',
      errorMsg: `Password is too short (Minimum ${PASSWORD_MIN_LENGTH} characters needed.)`,
    };
  }
  if (password.length > PASSWORD_MAX_LENGTH) {
    return {
      validateStatus: 'error',
      errorMsg: `Password is too long (Maximum ${PASSWORD_MAX_LENGTH} characters allowed.)`,
    };
  }
  const missing: string[] = [];
  if (!/\d/.test(password)) missing.push('number');
  if (!/[a-z]/.test(password)) missing.push('lowercase letter');
  if (!/[A-Z]/.test(password)) missing.push('uppercase letter');
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password))
    missing.push('special character');
  if (missing.length > 0) {
    return {
      validateStatus: 'error',
      errorMsg: `Password must contain at least one ${missing.join(', ')}`,
    };
  }
  return { validateStatus: 'success', errorMsg: null };
}

export function validateDateOfBirth(dateOfBirth: string): FieldValidation {
  if (!dateOfBirth.trim()) {
    return { validateStatus: 'error', errorMsg: 'Date of birth is required' };
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) {
    return {
      validateStatus: 'error',
      errorMsg: 'Date of birth must be a valid date (YYYY-MM-DD)',
    };
  }
  const [year, month, day] = dateOfBirth.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return {
      validateStatus: 'error',
      errorMsg: 'Date of birth must be a valid date (YYYY-MM-DD)',
    };
  }
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  if (parsed.getTime() >= todayStart.getTime()) {
    return {
      validateStatus: 'error',
      errorMsg: 'Date of birth must be in the past',
    };
  }
  return { validateStatus: 'success', errorMsg: null };
}

export interface SignUpFormData {
  name: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  dateOfBirth: string;
}

/**
 * Pre-submit validation for the sign-up form. Runs all field validators
 * and checks password confirmation. Returns a ClientValidationError when
 * validation fails, null when the form is valid.
 */
export function validateSignUpForm(
  data: SignUpFormData
): ClientValidationError | null {
  const { name, username, email, password, confirmPassword, dateOfBirth } =
    data;

  if (
    !name ||
    !username ||
    !email ||
    !password ||
    !confirmPassword ||
    !dateOfBirth
  ) {
    return createClientValidationError('Please fill in all fields', {
      ...(name ? {} : { name: 'Name is required' }),
      ...(username ? {} : { username: 'Username is required' }),
      ...(email ? {} : { email: 'Email is required' }),
      ...(password ? {} : { password: 'Password is required' }),
      ...(confirmPassword
        ? {}
        : { confirmPassword: 'Password confirmation is required' }),
      ...(dateOfBirth ? {} : { dateOfBirth: 'Date of birth is required' }),
    });
  }

  const nameResult = validateName(name);
  const usernameResult = validateUsername(username);
  const emailResult = validateEmail(email);
  const passwordResult = validatePassword(password);
  const dateOfBirthResult = validateDateOfBirth(dateOfBirth);

  if (
    nameResult.validateStatus === 'error' ||
    usernameResult.validateStatus === 'error' ||
    emailResult.validateStatus === 'error' ||
    passwordResult.validateStatus === 'error' ||
    dateOfBirthResult.validateStatus === 'error'
  ) {
    return createClientValidationError('Please fix the validation errors', {
      validation: 'Please fix the validation errors below',
    });
  }

  if (password !== confirmPassword) {
    return createClientValidationError('Passwords do not match', {
      confirmPassword: 'Passwords do not match',
    });
  }

  return null;
}
